import { useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { api, getToken, onUnauthorized, setApiTranslator, setToken } from "./lib/api";
import { disconnectSocket } from "./lib/socket";
import { buildTheme } from "./lib/theme";
import { useHashRoute } from "./lib/hooks";
import { I18nProvider, LangSwitch, useI18n, type MessageKey } from "./lib/i18n";
import { CountrySwitch } from "./components/CountrySwitch";
import { getCountry, setCountry, type Country } from "./lib/country";
import type { User } from "./lib/types";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { BrandLinks } from "./pages/BrandLinks";
import { Reports } from "./pages/Reports";
import { Devices } from "./pages/Devices";
import { DeviceChecks } from "./pages/DeviceChecks";
import { AppConfig } from "./pages/AppConfig";
import { NotificationChannels } from "./pages/NotificationChannels";
import { Users } from "./pages/Users";
import { Brands } from "./pages/Brands";
import { Roles } from "./pages/Roles";
import { IpRules } from "./pages/IpRules";
import { Logs } from "./pages/Logs";
import { AdminLogs } from "./pages/AdminLogs";
import { UserGuide } from "./pages/UserGuide";
import { OpenApiConnect } from "./pages/OpenApiConnect";
import { Loading } from "./components/ui";
import { NotificationsProvider, NotificationBell } from "./components/notifications";
import "./index.css";

const DRAWER_WIDTH = 232;

// `superadmin: true` = chỉ superadmin thấy (các thao tác toàn hệ thống, khớp
// SuperadminGuard ở backend). User brand chỉ thấy phần dữ liệu theo brand.
const NAV: { key: string; label: MessageKey; icon: string; superadmin?: boolean }[] = [
  { key: "dashboard", label: "nav.dashboard", icon: "◈" },
  { key: "links", label: "nav.links", icon: "🔗" },
  { key: "checks", label: "nav.checks", icon: "📡" },
  { key: "devices", label: "nav.devices", icon: "📱" },
  { key: "reports", label: "nav.reports", icon: "📊" },
  { key: "guide", label: "nav.guide", icon: "📖" },
  { key: "channels", label: "nav.channels", icon: "🔔", superadmin: true },
  { key: "users", label: "nav.users", icon: "👤", superadmin: true },
  { key: "brands", label: "nav.brands", icon: "🏷️", superadmin: true },
  { key: "roles", label: "nav.roles", icon: "🛡️", superadmin: true },
  { key: "iprules", label: "nav.iprules", icon: "🌐", superadmin: true },
  { key: "appconfig", label: "nav.appconfig", icon: "⬆️", superadmin: true },
  { key: "adminlogs", label: "nav.adminlogs", icon: "📋", superadmin: true },
  // Gọi thẳng service Domain Check Logger (khác gateway chính) bằng key tĩnh nhúng
  // trong bundle — chỉ superadmin thấy, xem src/lib/logsApi.ts.
  { key: "logs", label: "nav.logs", icon: "🧾", superadmin: true },
  { key: "openapi", label: "nav.openapi", icon: "⚡", superadmin: true },
];

/** Kiểm tra user có quyền truy cập mục menu hay không. */
export function canAccessNav(key: string, user: User): boolean {
  // openapi CHỈ dành riêng cho superadmin
  if (key === "openapi") {
    return user.role === "superadmin";
  }
  // user guide dành cho tất cả mọi quyền
  if (key === "guide") {
    return true;
  }
  if (user.role === "superadmin" || user.permissions?.includes("*")) return true;

  if (key === "roles") {
    return user.permissions?.includes("roles:read") ?? false;
  }
  if (key === "iprules") {
    return user.permissions?.includes("ip-rules:read") ?? false;
  }
  if (key === "adminlogs") {
    return user.permissions?.includes("admin-logs:read") ?? false;
  }
  if (key === "brands") {
    return (user.role === "admin" && user.country === "br") || (user.permissions?.includes("brands:read") ?? false);
  }
  if (key === "users") {
    return user.permissions?.includes("users:read") ?? false;
  }
  if (key === "channels") {
    return user.permissions?.includes("channels:read") ?? false;
  }
  if (key === "appconfig") {
    return user.permissions?.includes("app-config:read") ?? false;
  }
  if (key === "links") {
    return user.permissions ? user.permissions.includes("links:read") : true;
  }
  if (key === "checks") {
    return user.permissions ? user.permissions.includes("checks:read") : true;
  }
  if (key === "devices") {
    return user.permissions ? user.permissions.includes("devices:read") : true;
  }
  if (key === "reports") {
    return user.permissions ? user.permissions.includes("reports:read") : true;
  }
  if (key === "logs") {
    return false;
  }
  const item = NAV.find(n => n.key === key);
  if (!item?.superadmin) return true;
  return false;
}

/** Logo vuông bo góc với gradient — giữ nhận diện cũ (.logo-dot). */
function LogoMark() {
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "8px",
        flex: "none",
        background: "linear-gradient(135deg, #3b82f6, #6366f1)",
        boxShadow: "0 2px 8px rgb(59 130 246 / 0.4)",
      }}
    />
  );
}

function SidebarContent({
  user,
  path,
  onNavigate,
  onLogout,
}: {
  user: User;
  path: string;
  onNavigate: (to: string) => void;
  onLogout: () => void;
}) {
  const { t } = useI18n();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1.5, gap: 2.5 }}>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1.25, px: 1 }}>
        <LogoMark />
        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t("app.name")}</Typography>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontSize: 10, textTransform: "uppercase" }}
          >
            {t("app.cms")}
          </Typography>
        </Box>
        <Box sx={{ ml: "auto" }}>
          <NotificationBell navigate={onNavigate} />
        </Box>
      </Stack>

      <List sx={{ py: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {NAV.filter(item => canAccessNav(item.key, user)).map(item => (
          <ListItemButton
            key={item.key}
            selected={path === item.key}
            onClick={() => onNavigate(item.key)}
            sx={{ borderRadius: 2, py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 30, fontSize: 16, textAlign: "center" }}>
              <span aria-hidden>{item.icon}</span>
            </ListItemIcon>
            <ListItemText
              primary={t(item.label)}
              slotProps={{ primary: { sx: { fontSize: 13, fontWeight: path === item.key ? 600 : 500 } } }}
            />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
        <CountrySwitch user={user} />
        <LangSwitch />
        <Stack
          direction="row"
          sx={{ alignItems: "center", gap: 1.25, px: 1.25, py: 1, borderRadius: 2, bgcolor: "action.hover" }}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: "primary.main" }}>
            {user.username.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
              {user.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.role}
              {user.role !== "superadmin" && !(user.role === "admin" && user.country === "br") && user.brand ? ` · ${user.brand}` : ""}
            </Typography>
          </Box>
        </Stack>
        <Button
          color="inherit"
          onClick={onLogout}
          startIcon={<LogoutIcon fontSize="small" />}
          sx={{ justifyContent: "flex-start", color: "text.secondary" }}
        >
          {t("common.logout")}
        </Button>
      </Box>
    </Box>
  );
}

function Shell() {
  const { t } = useI18n();
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { route, navigate } = useHashRoute();

  // Có token sẵn trong localStorage thì xác thực lại trước khi vào app.
  useEffect(() => {
    if (!getToken()) {
      setBooting(false);
      return;
    }
    api.auth
      .me()
      .then(u => {
        setUser(u);
        if (u.country && !u.canSwitchCountry && getCountry() !== u.country) {
          setCountry(u.country as Country);
        }
      })
      .catch(() => {
        disconnectSocket();
        setToken(null);
      })
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    const handler = () => {
      disconnectSocket();
      setUser(null);
    };
    onUnauthorized.addEventListener("unauthorized", handler);
    return () => onUnauthorized.removeEventListener("unauthorized", handler);
  }, []);

  if (booting) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <Loading label={t("app.booting")} />
      </Box>
    );
  }

  // Response đăng nhập chưa có ngữ cảnh quốc gia (`canSwitchCountry`, danh sách
  // quốc gia) — vào app ngay rồi gọi /auth/me để bổ sung, tránh chớp màn hình.
  if (!user) {
    return (
      <Login
        onSuccess={u => {
          setUser(u);
          if (u.country && !u.canSwitchCountry && getCountry() !== u.country) {
            setCountry(u.country as Country);
          }
          api.auth
            .me()
            .then(me => {
              setUser(me);
              if (me.country && !me.canSwitchCountry && getCountry() !== me.country) {
                setCountry(me.country as Country);
              }
            })
            .catch(() => {});
        }}
      />
    );
  }

  const [rawPath = "", search = ""] = route.split("?");
  // Chặn user không đủ quyền truy cập path dù gõ thẳng hash → đưa về Tổng quan.
  const path =
    rawPath && !canAccessNav(rawPath, user)
      ? "dashboard"
      : rawPath;
  const params = new URLSearchParams(search);

  function logout() {
    disconnectSocket();
    setToken(null);
    setUser(null);
  }

  function go(to: string) {
    navigate(to);
    setMobileOpen(false);
  }

  const sidebar = (
    <SidebarContent user={user} path={path} onNavigate={go} onLogout={logout} />
  );

  return (
    <NotificationsProvider>
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Thanh trên chỉ hiện ở màn hình hẹp — mở/đóng Drawer tạm thời. */}
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          display: { md: "none" },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Toolbar variant="dense">
          <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label={t("app.cms")}>
            <MenuIcon />
          </IconButton>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1, ml: 0.5 }}>
            <LogoMark />
            <Typography sx={{ fontWeight: 700 }}>{t("app.name")}</Typography>
          </Stack>
          <Box sx={{ ml: "auto" }}>
            <NotificationBell navigate={go} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer cố định cho desktop. */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderColor: "divider",
            bgcolor: "background.paper",
          },
        }}
        open
      >
        {sidebar}
      </Drawer>

      {/* Drawer tạm thời cho mobile. */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            bgcolor: "background.paper",
          },
        }}
      >
        {sidebar}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 3.5 },
          py: { xs: 2, md: 3 },
          pb: 6,
        }}
      >
        {/* Đệm cho AppBar cố định trên mobile. */}
        <Toolbar variant="dense" sx={{ display: { md: "none" } }} />
        {path === "links" ? (
          <BrandLinks navigate={go} me={user} />
        ) : path === "checks" ? (
          <DeviceChecks key={search} params={params} role={user.role} />
        ) : path === "devices" ? (
          <Devices navigate={go} />
        ) : path === "reports" ? (
          <Reports />
        ) : path === "guide" ? (
          <UserGuide />
        ) : path === "channels" ? (
          <NotificationChannels />
        ) : path === "users" ? (
          <Users meId={user.id} />
        ) : path === "brands" ? (
          <Brands />
        ) : path === "roles" ? (
          <Roles />
        ) : path === "iprules" ? (
          <IpRules />
        ) : path === "appconfig" ? (
          <AppConfig />
        ) : path === "adminlogs" ? (
          <AdminLogs />
        ) : path === "logs" ? (
          <Logs />
        ) : path === "openapi" ? (
          user.role === "superadmin" ? (
            <OpenApiConnect user={user} />
          ) : (
            <Dashboard navigate={go} />
          )
        ) : (
          <Dashboard navigate={go} />
        )}
      </Box>
    </Box>
    </NotificationsProvider>
  );
}

/** Cầu nối để client API (nằm ngoài React) dùng được bảng dịch hiện hành. */
function ApiTranslatorBridge() {
  const { t } = useI18n();
  useEffect(() => {
    setApiTranslator((key, vars) => t(key as MessageKey, vars));
  }, [t]);
  return null;
}

/** Theme MUI bám theo ngôn ngữ (locale của DataGrid) và chế độ sáng/tối của hệ điều hành. */
function ThemedApp() {
  const { lang } = useI18n();
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = useMemo(
    () => buildTheme(prefersDark ? "dark" : "light", lang),
    [prefersDark, lang],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Shell />
    </ThemeProvider>
  );
}

export function App() {
  return (
    <I18nProvider>
      <ApiTranslatorBridge />
      <ThemedApp />
    </I18nProvider>
  );
}

export default App;
